import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import api from '../../services/api';

/**
 * Inline form in the note link dialog that drafts a new action item, on an
 * existing task or a new one. Nothing is saved here: the draft is created with
 * the time logs when the link dialog is saved.
 * @param {Object} props
 * @param {string} props.initialDescription
 * @param {{id: number|string, name: string, isNew?: boolean}[]} props.tasks - Existing and drafted tasks
 * @param {number} props.workspaceId
 * @param {Function} props.onCancel
 * @param {Function} props.onCreate - ``({description, task})`` for a picked task, or
 *   ``({description, newTask: {name, track_id}})`` for a new one
 */
const NewActionItemForm = ({ initialDescription, tasks, workspaceId, onCancel, onCreate }) => {
  const [description, setDescription] = useState(initialDescription);
  const [mode, setMode] = useState(tasks.length ? 'existing' : 'new');
  const [task, setTask] = useState(null);
  const [taskName, setTaskName] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState('');

  useEffect(() => {
    if (mode !== 'new' || !workspaceId || projects.length) return;
    api
      .get('/filtered-projects/', { params: { workspace_id: workspaceId } })
      .then((response) => setProjects(response.data.payload || response.data || []))
      .catch((err) => console.error('Error fetching projects:', err));
  }, [mode, workspaceId, projects.length]);

  useEffect(() => {
    setTrackId('');
    if (!projectId) {
      setTracks([]);
      return;
    }
    api
      .get('/filtered-tracks/', { params: { workspace_id: workspaceId, project_id: projectId } })
      .then((response) => {
        const fetched = response.data.payload || response.data || [];
        setTracks(fetched);
        // One track is the common case: pick it so the user can go straight to Add.
        if (fetched.length === 1) setTrackId(fetched[0].id);
      })
      .catch((err) => console.error('Error fetching tracks:', err));
  }, [projectId, workspaceId]);

  const valid = description.trim() && (mode === 'existing' ? task : taskName.trim() && trackId);

  const handleCreate = () => {
    if (!valid) return;
    if (mode === 'existing') {
      onCreate({ description: description.trim(), task });
    } else {
      onCreate({ description: description.trim(), newTask: { name: taskName.trim(), track_id: trackId } });
    }
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleCreate();
      }}
      sx={{ p: 1.5, border: 1, borderColor: 'primary.light', borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">New action item</Typography>
        <TextField
          label="Action item"
          size="small"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          fullWidth
          autoFocus
        />
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, value) => value && setMode(value)}>
          <ToggleButton value="existing" disabled={!tasks.length}>
            Existing task
          </ToggleButton>
          <ToggleButton value="new">New task</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'existing' ? (
          <Autocomplete
            size="small"
            options={tasks}
            value={task}
            onChange={(_, value) => setTask(value)}
            getOptionLabel={(option) => (option.isNew ? `${option.name} (new)` : option.name)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            openOnFocus
            renderInput={(params) => <TextField {...params} label="Task" />}
          />
        ) : (
          <>
            <TextField
              label="Task name"
              size="small"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                select
                size="small"
                label="Project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                fullWidth
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Track"
                value={trackId}
                onChange={(e) => setTrackId(e.target.value)}
                disabled={!projectId}
                fullWidth
              >
                {tracks.map((track) => (
                  <MenuItem key={track.id} value={track.id}>
                    {track.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="small" variant="contained" type="submit" disabled={!valid}>
            Add
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default NewActionItemForm;
